import React, { useEffect, useMemo, useRef, useState } from "react";
import { millingAsset } from "../../../model/millingAssets";
import api from "../../../data/api/mainApi";

// The backend allows one active DXF task per user; previews share a serial queue.
let renderQueue=Promise.resolve();
function loadSolid(key){
  const result=renderQueue.then(()=>millingAsset(key,"3d"));
  renderQueue=result.catch(()=>{});
  return result;
}

export function previewDimensions(entry, dimensions) {
  const name=entry.name.normalize("NFD").replace(/\p{M}/gu, "").trim().toLowerCase();
  if(entry.id!=="main" && name==="fiokelo") return {...dimensions,height:145};
  if(entry.id!=="main" && name==="joly") return {...dimensions,width:145};
  return dimensions;
}

export default function MillingEditorPreview({ milling }) {
  const canvas=useRef(null), viewer=useRef(null), cache=useRef(new Map()), uploads=useRef(new WeakMap());
  const [ready,setReady]=useState(false), [assets,setAssets]=useState({}), [errors,setErrors]=useState({});
  const [renderErrors,setRenderErrors]=useState({}), [fatal,setFatal]=useState(""), [loading,setLoading]=useState(false), [retry,setRetry]=useState(0);
  const [dimensions,setDimensions]=useState({width:500,height:700,depth:18});
  const [selectedId,setSelectedId]=useState(null);
  const preview=useRef(null);
  const [fullscreen,setFullscreen]=useState(false),[fullscreenError,setFullscreenError]=useState("");
  useEffect(()=>{
    const changed=()=>setFullscreen(document.fullscreenElement===preview.current);
    document.addEventListener("fullscreenchange",changed);
    return()=>document.removeEventListener("fullscreenchange",changed);
  },[]);
  const toggleFullscreen=async()=>{
    setFullscreenError("");
    try {
      if(document.fullscreenElement===preview.current)await document.exitFullscreen();
      else if(preview.current?.requestFullscreen)await preview.current.requestFullscreen();
      else throw new Error("unsupported");
    }catch {setFullscreenError("A böngésző nem engedélyezte a teljes képernyős nézetet.");}
  };
  const entries=useMemo(()=>[{id:"main",name:"Standard",value:milling},...(milling.variants||[]).map(v=>({id:v.variantId,name:v.name,value:v}))],[milling]);
  const activeId=entries.some(e=>e.id===selectedId)?selectedId:null;
  const sources=useMemo(()=>entries.map(e=>({id:e.id,key:e.value.dxfKey,file:e.value.dxfFile})),[entries]);
  // Keep source loading independent of name, stretch and repetition edits.
  const sourceSignature=sources.map(s=>`${s.id}:${s.key||""}:${s.file?.name||""}:${s.file?.size||""}:${s.file?.lastModified||""}`).join("|");
  const latestSources=useRef(sources);latestSources.current=sources;
  useEffect(()=>{
    let active=true;
    import("./millingPreviewScene").then(({createPreviewScene})=>{
      if(!active)return;
      try {viewer.current=createPreviewScene(canvas.current);setReady(true);}catch(e){setFatal("A 3D-előnézethez WebGL-t támogató böngésző szükséges.");}
    }).catch(()=>{if(active)setFatal("A 3D-megjelenítő nem tölthető be.");});
    return ()=>{active=false;viewer.current?.dispose();viewer.current=null;};
  },[]);
  useEffect(()=>{
    if(!ready)return undefined;
    let active=true;
    setLoading(true);setAssets({});setErrors({});
    const load=async()=>{
      const next={},failures={};
      for(const source of latestSources.current){
        if(!active)break;
        try {
          let key=source.key;
          if(source.file){
            if(!uploads.current.has(source.file)){
              const body=new FormData();body.append("file",source.file);
              uploads.current.set(source.file,api.post("/millings/dxf",body).then(r=>r.data.dxfKey).catch(e=>{uploads.current.delete(source.file);throw e;}));
            }
            key=await uploads.current.get(source.file);
          }
          if(!key)throw new Error("Nincs DXF; valódi 3D-előnézet nem készíthető.");
          if(!cache.current.has(key))cache.current.set(key,loadSolid(key).catch(e=>{cache.current.delete(key);throw e;}));
          next[source.id]={key,file:source.file,asset:await cache.current.get(key)};
        }catch(e){failures[source.id]=e.message||"A 3D betöltése nem sikerült.";}
      }
      if(active){setAssets(next);setErrors(failures);setLoading(false);}
    };
    load();return ()=>{active=false;};
  },[sourceSignature,ready,retry]);
  useEffect(()=>{
    if(!viewer.current||!ready)return undefined;
    const timer=setTimeout(()=>{
      const valid=Object.values(dimensions).every(v=>Number.isFinite(v)&&v>0&&v<=10000);
      if(!valid)return;
      const items=entries.filter(e=>activeId===null||e.id===activeId).map(e=>({...e,dimensions:previewDimensions(e,dimensions),asset:assets[e.id]?.file===e.value.dxfFile&&(e.value.dxfFile||assets[e.id]?.key===e.value.dxfKey)?assets[e.id]?.asset:null}));
      try{setRenderErrors(viewer.current.show(items,dimensions.width,dimensions.height,dimensions.depth));setFatal("");}
      catch(e){setFatal(e.message||"Az előnézet nem készíthető el.");}
    },250);
    return ()=>clearTimeout(timer);
  },[entries,assets,dimensions,ready,activeId]);
  return <section ref={preview} className="border rounded p-2 mb-3" aria-label="Marás 3D-előnézete"
    style={{background:"#fff",...(fullscreen?{width:"100%",height:"100%",overflowY:"auto"}: {})}}>
    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
      <h3 className="h5 mb-0">3D-előnézet – fő minta és minden változat</h3>
      <button type="button" className="btn btn-sm btn-outline-primary" aria-pressed={fullscreen} onClick={toggleFullscreen}>
        {fullscreen?"Kilépés a teljes képernyőből":"Teljes képernyő"}
      </button>
    </div>
    {fullscreenError&&<p role="alert" className="text-warning">{fullscreenError}</p>}
    <div className="d-flex flex-wrap gap-2">{[["width","Szélesség"],["height","Magasság"],["depth","Vastagság"]].map(([key,label])=><label key={key}>{label} (mm)
      <input className="form-control" aria-label={`Előnézet ${label.toLowerCase()}`} style={{width:130}} type="number" min="1" max="10000" value={dimensions[key]}
        onChange={e=>setDimensions({...dimensions,[key]:e.target.value===""?"":Number(e.target.value)})} /></label>)}</div>
    <small className="d-block text-muted">Forgatás: húzás; nagyítás: görgő. A Fiókelő magassága 145 mm, szélessége a Standardét követi. A Joly szélessége 145 mm, magassága a Standardét követi.
      A nyújtás, ismétlés és átmenő kivágás a created item megjelenítőjének geometriájával készül. Az új DXF-et az előnézethez feltöltjük; a marás mentése külön történik.</small>
    <canvas ref={canvas} aria-label="Marásváltozatok 3D nézete" style={{width:"100%",height:fullscreen?"calc(100dvh - 260px)":420,minHeight:fullscreen?240:undefined,display:"block",touchAction:"none"}} />
    {!Object.values(dimensions).every(v=>Number.isFinite(v)&&v>0&&v<=10000)&&<p role="alert" className="text-warning">Az előnézet méretei 0 és 10000 mm közötti pozitív számok legyenek.</p>}
    {loading&&<p role="status">A fő minta és a változatok 3D-előnézetének betöltése…</p>}
    {fatal&&<p role="alert" className="text-danger">{fatal}</p>}
    <div className="d-flex gap-3 flex-wrap" role="group" aria-label="Megjelenített változat">
      <button type="button" className={`btn btn-sm ${activeId===null?"btn-primary":"btn-outline-primary"}`} aria-pressed={activeId===null} onClick={()=>setSelectedId(null)}>Összes változat</button>
      {entries.map((e,i)=><div key={e.id}><button type="button" className={`btn btn-sm ${activeId===e.id?"btn-primary":"btn-outline-primary"}`} aria-pressed={activeId===e.id} onClick={()=>setSelectedId(e.id)}>{i+1}. {e.name}</button>
      {(errors[e.id]||renderErrors[e.id])&&<p role="alert" className="text-warning">{errors[e.id]||renderErrors[e.id]}</p>}</div>)}</div>
    {!!Object.keys(errors).length&&<button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading} onClick={()=>setRetry(v=>v+1)}>3D betöltés újrapróbálása</button>}
  </section>;
}
